

































import 'intl-tel-input/build/css/intlTelInput.css';

import type { Ref } from '@nuxtjs/composition-api';
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  ref,
  watch,
  watchEffect
} from '@nuxtjs/composition-api';
import type { Iti } from 'intl-tel-input';
import IntlTelInput from 'intl-tel-input';
import { nanoid } from 'nanoid';

import { useConfig } from '~/composables';
import { useIPData } from '~/diptyqueTheme/composable/useIPData';
import { usePhoneMask } from '~/diptyqueTheme/composable/usePhoneMask';

export default defineComponent({
  name: 'VaimoPhoneInput',
  props: {
    value: {
      type: String,
      default: ''
    },
    autocomplete: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    strictMode: {
      type: Boolean,
      default: false
    },
    separateDialCode: {
      type: Boolean,
      default: false
    },
    showFlags: {
      type: Boolean,
      default: true
    },
    showSearchBox: {
      type: Boolean,
      default: true
    },
    useFullscreenPopup: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input', 'onValidate', 'onFocus', 'init'],
  setup(props, { emit }) {
    const phoneInput: Ref<HTMLInputElement | null> = ref(null);
    const itiInstance = ref<Iti | null>(null);
    const formattedValue: Ref<string> = ref('');
    const errorMessage: Ref<string> = ref('');
    const showError: Ref<boolean> = ref(false);
    const { userCountry } = useIPData();
    const { config } = useConfig();
    const { setPrefix } = usePhoneMask();

    const inputId = `vaimo-phone-input-${nanoid()}`;

    const getCountryCode = computed(() => {
      return config.value?.general_country_default === 'NL'
        ? 'US'
        : userCountry.value?.toLowerCase() || 'auto';
    });

    const isTelInputReady = ref(false);

    const initIntlTelInput = async (country: string) => {
      if (!process.client || !phoneInput.value || itiInstance.value) return;

      itiInstance.value = IntlTelInput(phoneInput.value, {
        initialCountry: getCountryCode.value,
        strictMode: props.strictMode,
        separateDialCode: props.separateDialCode,
        showFlags: props.showFlags,
        countrySearch: props.showSearchBox,
        geoIpLookup: (success, failure) => {
          country ? success(country.toLowerCase()) : failure();
        },
        loadUtils: () => import('intl-tel-input/build/js/utils.js'),
        useFullscreenPopup: props.useFullscreenPopup
      });

      await itiInstance.value.promise;

      if (props.value !== undefined) {
        const cleanedPhone = props.value.replace(/\s+/g, '');
        const phoneNumberWithPrefix = setPrefix(cleanedPhone);
        itiInstance.value.setNumber(phoneNumberWithPrefix);
        formattedValue.value = phoneInput.value.value;
      }

      emit('init');
      isTelInputReady.value = true;

      if (formattedValue.value?.trim()) {
        validatePhoneNumber();
      }
    };

    const validatePhoneNumber = () => {
      if (!itiInstance.value) return;

      const isEmpty = !formattedValue.value?.trim();

      if (isEmpty) {
        errorMessage.value = 'This field is required';
        showError.value = true;
        emit('onValidate', { valid: false });
        return;
      }

      if (!itiInstance.value.isValidNumber()) {
        errorMessage.value =
          'Please enter a valid phone number (example: +XXXXXX...)';
        showError.value = true;
        emit('onValidate', { valid: false });
        return;
      }

      showError.value = false;
      emit('onValidate', { valid: true });
    };

    const handleFocus = () => emit('onFocus');

    const handlePaste = (event: ClipboardEvent) => {
      if (!process.client) return;

      const pasted = event.clipboardData?.getData('text')?.trim();

      if (pasted && itiInstance.value && phoneInput.value) {
        phoneInput.value.value = '';
        itiInstance.value.setNumber(pasted);
        formattedValue.value = phoneInput.value.value;
        emit('input', itiInstance.value.getNumber());

        validatePhoneNumber();
      }
    };

    const handleInput = (event: Event) => {
      if (!isTelInputReady.value) return;

      const target = event.target as HTMLInputElement;
      formattedValue.value = target.value;

      const internationalPhoneNumber = itiInstance.value.getNumber();
      if (itiInstance.value && internationalPhoneNumber) {
        emit('input', internationalPhoneNumber);
      }

      validatePhoneNumber();
    };

    const handleBlur = () => validatePhoneNumber();

    watch(
      () => props.value,
      (newVal) => {
        if (process.client && newVal !== undefined && itiInstance.value) {
          const phoneNumberWithPrefix = newVal ? setPrefix(newVal) : '';
          itiInstance.value.setNumber(phoneNumberWithPrefix);
          formattedValue.value = phoneInput.value?.value || '';
        }
      }
    );

    watchEffect(() => {
      if (process.client && userCountry.value && !itiInstance.value) {
        initIntlTelInput(userCountry.value);
      }
    });

    const resetValidation = () => {
      errorMessage.value = '';
      showError.value = false;
    };

    onBeforeUnmount(() => {
      if (itiInstance.value) {
        itiInstance.value.destroy();
        itiInstance.value = null;
      }
    });

    return {
      phoneInput,
      inputId,
      formattedValue,
      handleFocus,
      handlePaste,
      handleInput,
      errorMessage,
      showError,
      handleBlur,
      resetValidation
    };
  }
});
